import { Chart, useChart } from '@chakra-ui/charts';
import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex, FormatNumber, HStack, Stat, VStack } from '@chakra-ui/react';
import { Area, AreaChart } from 'recharts';
import { Tooltip } from '@/components/ui/tooltip';
import { TrendBadge } from './TrendBadge';

// Define the Nivo Serie type for backward compatibility
type NivoDataPoint = { x: string | number; y: number };
type NivoSerie = { id: string | number; data: ReadonlyArray<NivoDataPoint> };

interface StatCardProps extends BoxProps {
  title: string;
  description: string;
  value?: string | number;
  currency?: string;
  percentageChange?: number;
  chartData?: NivoSerie[] | null;
  background?: string;
  comparisonText?: string;
  isLoading?: boolean;
  showBorder?: boolean;
  borderRadius?: string;
  /** Optional concise explainer; renders the title with a dotted underline + tooltip. */
  infoTip?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  currency,
  percentageChange,
  chartData,
  background = 'white',
  comparisonText,
  isLoading,
  showBorder = true,
  borderRadius = 'xl',
  infoTip,
  ...props
}) => {
  // Convert Nivo chart data to Chakra UI chart data format
  const convertChartData = () => {
    if (!chartData || chartData.length === 0) return null;

    // Use the first series data (most common case)
    const firstSeries = chartData[0];
    return firstSeries.data.map((point) => ({
      value: point.y,
      label: point.x,
    }));
  };

  // Setup Chakra UI chart
  const transformedData = convertChartData();
  const chartConfig = useChart({
    data: transformedData || [],
    series: [{ name: 'value', color: 'primary.solid' }],
  });
  const chart = chartData && transformedData ? chartConfig : null;

  return (
    <Box
      py={4}
      px={4}
      rounded={borderRadius}
      shadow={showBorder ? 'card' : 'none'}
      border={showBorder ? '1px solid' : 'none'}
      borderColor={showBorder ? 'light-gray-2' : 'transparent'}
      bg={background}
      height="100%"
      w="full"
      opacity={isLoading ? 0.6 : 1}
      transition="opacity 0.2s ease-in-out"
      {...props}
    >
      <Stat.Root>
        {/* Header with title and icon */}
        <HStack justify="space-between">
          {infoTip ? (
            <Tooltip content={infoTip} showArrow portalled positioning={{ placement: 'top' }}>
              <Stat.Label
                fontFamily="heading"
                textStyle="sm"
                fontWeight="medium"
                color="gray.fg"
                w="fit-content"
                cursor="help"
                css={{
                  textDecorationLine: 'underline',
                  textDecorationStyle: 'dotted',
                  textDecorationColor: 'currentColor',
                  textUnderlineOffset: '3px',
                }}
              >
                {title}
              </Stat.Label>
            </Tooltip>
          ) : (
            <Stat.Label fontFamily="heading" textStyle="sm" fontWeight="medium" color="gray.fg">
              {title}
            </Stat.Label>
          )}
        </HStack>

        {/* Main content area */}
        <Flex justify="space-between" align="end">
          <VStack alignItems="start" gap={2} flex={1}>
            {/* Main Value */}
            <Flex align="center" gapX={2}>
              <Stat.ValueText fontWeight="semibold">
                {typeof value === 'string' ? (
                  value
                ) : (
                  <FormatNumber
                    value={value as number}
                    style={currency ? 'currency' : 'decimal'}
                    currency={currency}
                  />
                )}
              </Stat.ValueText>
              {percentageChange !== undefined && (
                <TrendBadge percentageChange={percentageChange} useFormatNumber={true} />
              )}
            </Flex>
            {/* Comparison and Percentage Change */}
            <HStack gap={2} alignItems="center">
              {comparisonText && (
                <Stat.HelpText
                  textStyle="xs"
                  color="gray.500"
                  whiteSpace="nowrap"
                  textTransform="uppercase"
                >
                  {comparisonText}
                </Stat.HelpText>
              )}
            </HStack>
          </VStack>

          {/* Chart - right side */}
          {chart && transformedData && (
            <Box h="40px" w="120px" overflow="hidden" ml={4}>
              <Chart.Root width="100%" height="40px" chart={chart}>
                <AreaChart data={chart.data}>
                  {chart.series.map((item) => (
                    <Area
                      key={item.name}
                      isAnimationActive={false}
                      dataKey={chart.key(item.name)}
                      fill={chart.color(item.color)}
                      fillOpacity={0.2}
                      stroke={chart.color(item.color)}
                      strokeWidth={2}
                      type="linear"
                    />
                  ))}
                </AreaChart>
              </Chart.Root>
            </Box>
          )}
        </Flex>
      </Stat.Root>
    </Box>
  );
};

export default StatCard;
